import styled from "@emotion/styled";
import { useTimeoutEffect } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useState } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { googleImaAtom } from "scmp-app/components/advertisement/google-ima/atoms";
import { useIntersection } from "scmp-app/lib/hooks";

import { VideoPlayMode } from "./enums";
import type { VideoTitleContainerProps } from "./scmp-youtube-video-footer/styles";
import type { Props as SCMPYouTubeVideoPlayerProps } from "./scmp-youtube-video-player";
import { SCMPYouTubeVideoPlayer } from "./scmp-youtube-video-player";
import {
  Container,
  PlayerContainer,
  StyledSCMPYouTubeVideoFooter,
  StyledSCMPYoutubeVideoPreview,
} from "./styles";

export type Props = Omit<
  SCMPYouTubeVideoPlayerProps,
  "queryReference" | "videoId" | "videoPlayMode"
> & {
  className?: string;
  duration?: string;
  enableAutoPlay: boolean;
  imageSource?: string;
  index: number;
  initialVideoPlayMode?: VideoPlayMode;
  isEnabledImaManager: boolean;
  isMuteVideo?: boolean;
  withFooter?: { variant: VideoTitleContainerProps["$variant"]; withIcon?: boolean };
  youtubeVideoId?: string;
};

const Component: FunctionComponent<Props> = ({
  className,
  duration,
  enableAutoPlay,
  height,
  imageSource,
  index = 0,
  initialVideoPlayMode = VideoPlayMode.Unset,
  isEnabledImaManager,
  isMuteVideo = true,
  title,
  width,
  withFooter,
  youtubeVideoId,
  ...attribs
}: Props) => {
  const { isImaReady } = useAtomValue(googleImaAtom);

  const hasVideoId = notEmpty(youtubeVideoId);
  const [videoPlayMode, setVideoPlayMode] = useState(initialVideoPlayMode);
  const [shouldShowPlayer, setShouldShowPlayer] = useState(false);
  const [shouldShowPreview, setShouldShowPreview] = useState(true);
  const [previewTimeout, setPreviewTimeout] = useState<number>();

  const orientation =
    attribs.orientation ??
    (height && width && Number.parseInt(height, 10) > Number.parseInt(width, 10)
      ? "portrait"
      : "landscape");

  const handlePlaybackVisible = useCallback(() => {
    setShouldShowPlayer(true);
  }, []);

  const handlePreviewClick = useCallback(() => {
    setVideoPlayMode(VideoPlayMode.Manual);
  }, []);

  useTimeoutEffect(() => {
    handlePlaybackVisible();
  }, previewTimeout);

  useEffect(() => {
    if (hasVideoId && isImaReady && !shouldShowPlayer) setPreviewTimeout(1000);
    if (!hasVideoId || !isImaReady || !shouldShowPlayer) return;
    setShouldShowPreview(false);
  }, [hasVideoId, isImaReady, shouldShowPlayer]);

  const { captureIntersectionElement, intersection } = useIntersection({
    threshold: 0.45,
  });
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));

  useEffect(() => {
    if (
      enableAutoPlay &&
      intersection?.isIntersecting &&
      initialVideoPlayMode === VideoPlayMode.Unset &&
      isDesktopUp
    ) {
      setVideoPlayMode(VideoPlayMode.Autoplay);
    }
  }, [enableAutoPlay, intersection?.isIntersecting, isDesktopUp, initialVideoPlayMode]);

  return (
    <Container className={className} ref={captureIntersectionElement}>
      <PlayerContainer $orientation={orientation}>
        {hasVideoId && (
          <ClientSideSuspense>
            <SCMPYouTubeVideoPlayer
              enableAutoPlay={enableAutoPlay}
              height={height}
              index={index}
              isEnabledImaManager={isEnabledImaManager}
              isMuteVideo={isMuteVideo}
              onPlaybackVisible={handlePlaybackVisible}
              orientation={orientation}
              title={title}
              videoId={youtubeVideoId}
              videoPlayMode={videoPlayMode}
              width={width}
              {...attribs}
            />
          </ClientSideSuspense>
        )}
        {shouldShowPreview && (
          <StyledSCMPYoutubeVideoPreview
            duration={duration}
            imageSource={imageSource}
            onClick={handlePreviewClick}
            orientation={orientation}
          />
        )}
      </PlayerContainer>
      {withFooter && (
        <StyledSCMPYouTubeVideoFooter
          $orientation={orientation}
          title={title}
          variant={withFooter.variant}
          withIcon={withFooter.withIcon}
        />
      )}
    </Container>
  );
};

Component.displayName = "SCMPYoutubeVideo";
export const SCMPYoutubeVideo = styled(Component)``;
